import React from "react";
import PropTypes from "prop-types";
import {
  WarningAmber as WarningAmberIcon,
  ExpandMore,
  ErrorOutline,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf,
} from "@mui/icons-material";
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  getComplianceAlertConfig,
  getComplianceChipColor,
  getComplianceChipLabel,
  getComplianceDialogActionsVariant,
  getComplianceIssueColor,
  getComplianceIssueIconType,
  getComplianceTitleIconType,
  getFileValidationBorderColor,
  getFileValidationIconColor,
} from "../pgHomeComplianceHelpers";

function ComplianceTitleIcon({ hasBlockingErrors, hasPiiWarningsOnly }) {
  const iconType = getComplianceTitleIconType(hasBlockingErrors, hasPiiWarningsOnly);
  if (iconType === "error") return <ErrorOutline color="error" />;
  if (iconType === "warning") return <WarningAmberIcon sx={{ color: "warning.main" }} />;
  return <CheckCircleIcon color="success" />;
}

function IssueStatusIcon({ status, isPiiWarning }) {
  const iconType = getComplianceIssueIconType(status, isPiiWarning);
  if (iconType === "warning") return <WarningAmberIcon color="warning" fontSize="small" />;
  if (iconType === "error") return <ErrorOutline color="error" fontSize="small" />;
  return <CheckCircleIcon color="success" fontSize="small" />;
}

function ComplianceDialogActions({
  variant,
  onClose,
  onFinalize,
  isFinalizing,
}) {
  if (variant === "blocking") {
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          Close this report to address issues and upload corrected files.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </Box>
      </>
    );
  }

  if (variant === "piiWarning") {
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          Double-check for PII exposure; you may proceed if appropriate.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
          <Button variant="contained" color="warning" onClick={onFinalize} disabled={isFinalizing}>
            {isFinalizing ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} /> Submitting...
              </>
            ) : (
              "Proceed"
            )}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        Documents are ready to submit.
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onFinalize} disabled={isFinalizing}>
          {isFinalizing ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} /> Submitting...
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </Box>
    </>
  );
}

/** Indemnity PDF validation results (compliance-check) modal. */
export default function PgHomeCompliancePanel({
  open,
  onClose,
  validationResults,
  hasBlockingErrors,
  hasPiiWarningsOnly,
  onFinalize,
  isFinalizing,
}) {
  const alertConfig = getComplianceAlertConfig(hasBlockingErrors, hasPiiWarningsOnly);
  const dialogActionsVariant = getComplianceDialogActionsVariant(hasBlockingErrors, hasPiiWarningsOnly);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ComplianceTitleIcon
          hasBlockingErrors={hasBlockingErrors}
          hasPiiWarningsOnly={hasPiiWarningsOnly}
        />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Indemnity Validation Report
        </Typography>
        <Chip size="small" label="Probate Bond Pros AI" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5 }}>
        <Alert severity={alertConfig.severity} sx={{ mb: 2 }}>
          {alertConfig.message}
        </Alert>
        <Stack spacing={1.25}>
          {validationResults.map((file, idx) => {
            const hasBlocking = file.issues.some((i) => i.status === "fail" && i.key !== "pii");
            const hasPiiOnly = file.issues.some((i) => i.status === "fail" && i.key === "pii");
            const borderColor = getFileValidationBorderColor(hasBlocking, hasPiiOnly);
            const iconColor = getFileValidationIconColor(hasBlocking, hasPiiOnly);
            return (
              <Paper key={`${file.name}-${idx}`} elevation={0} sx={{ p: 1.25, borderRadius: 1, border: borderColor }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PictureAsPdf sx={{ color: iconColor }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {file.sizeKb} KB
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {file.issues.map((iss, i) => {
                      const isPiiWarning = iss.key === "pii" && iss.status === "fail";
                      const chipColor = getComplianceChipColor(iss.status, isPiiWarning);
                      const chipLabel = getComplianceChipLabel(iss.title, iss.status, isPiiWarning);
                      return (
                        <Chip key={`${iss.key}-${i}`} size="small" color={chipColor} variant="outlined" label={chipLabel} />
                      );
                    })}
                  </Box>
                </Box>
                {file.issues.map((iss, i) => {
                  const isPiiWarning = iss.key === "pii" && iss.status === "fail";
                  const issueColor = getComplianceIssueColor(iss.status, isPiiWarning);
                  return (
                    <Accordion key={`${iss.key}-acc-${i}`} elevation={0} disableGutters sx={{ borderRadius: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IssueStatusIcon status={iss.status} isPiiWarning={isPiiWarning} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {iss.title}
                          </Typography>
                          <Chip size="small" color={issueColor} label={iss.summary} sx={{ ml: 0.5 }} />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {iss.status === "fail" ? (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: 1.5,
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                What we found
                              </Typography>
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {iss.details.map((d) => (
                                  <Typography key={`${iss.key}-found-${d}`} variant="body2">
                                    • {d}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                How to fix
                              </Typography>
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {iss.nextSteps.map((n) => (
                                  <Typography key={`${iss.key}-fix-${n}`} variant="body2">
                                    • {n}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              What we found
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                              {iss.details.map((d) => (
                                <Typography key={`${iss.key}-pass-${d}`} variant="body2">
                                  • {d}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <ComplianceDialogActions
          variant={dialogActionsVariant}
          onClose={onClose}
          onFinalize={onFinalize}
          isFinalizing={isFinalizing}
        />
      </DialogActions>
    </Dialog>
  );
}

const issueShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  title: PropTypes.string,
  status: PropTypes.string,
  summary: PropTypes.string,
  details: PropTypes.arrayOf(PropTypes.any),
  nextSteps: PropTypes.arrayOf(PropTypes.any),
});

PgHomeCompliancePanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  validationResults: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      sizeKb: PropTypes.number,
      issues: PropTypes.arrayOf(issueShape),
    })
  ).isRequired,
  hasBlockingErrors: PropTypes.bool.isRequired,
  hasPiiWarningsOnly: PropTypes.bool.isRequired,
  onFinalize: PropTypes.func.isRequired,
  isFinalizing: PropTypes.bool.isRequired,
};
